import * as React from "react";
import {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {createRoot} from "react-dom/client";
import {getShopId} from "../getShopId";
import {isoLocales, LocalDataType} from "../payments/PaymentController";
import {cdnDomain} from "../../../helpers/cnxDomains";
import {BrowserStorage} from "../../../storage/BrowserStorage";
import window from "../WindowExtended";
import Modal from "../../lib/ModalWindow/Modal";

/**
 * A modal to confirm that the customer is currently using their preferred sales channel
 * Don't show the modal when:
 * 1) The preferred browser language setting of the user matches the current shop language
 * 2) The preferred browser language setting has no country code but still technically matches the shop language
 *    For example: When the browser language setting is just "de" and you are on the DE, AT or CH_DE shop
 * 3) The country code of the browser language matches the country code of the current shop
 *    For example: The browser language setting is "en-DE" and you are currently on the DE shop
 * 4) A "countryPreference" setting can be found in the local storage (e.g. when the customer previously interacted with the Geo Location Modal)
 * 5) The user is on the COM sales channel and their preferred browser language doesn't match another sales channels' language
 * 6) The user hasn't interacted with our cookie banner yet
 *    This is checked by looking for the local storage key "uc_user_interaction" (which is set by Usercentrics)
 *    If "uc_user_interaction" doesn't exist yet / is falsy, then we are using an (self-created) Usercentrics Event, called "ucEvent" (https://usercentrics.com/docs/web/features/events/consent-events/)
 */

type ShopInfo = {
  name: string;
  url: string;
  iconUrl: string | null;
};

const browserStorage = new BrowserStorage<string>({
  type: "localStorage",
  dataIdentifier: "countryPreference",
});

const shopId: number = getShopId();
const shopLocale = isoLocales[shopId];
const browserLocale = navigator.language;

class GeoLocationModalPortal {
  public static run() {
    // Already on the right sales channel. No further investigation necessary
    if (browserLocale === shopLocale.locale) {
      return;
    }

    const geoLocationDiv = document.getElementById("GeoLocationModal");
    const storedCountryPreference = browserStorage.read("isoLocale");

    if (!geoLocationDiv || storedCountryPreference === shopLocale.locale) {
      return;
    }

    // check if we even have a sales channel that uses the preferred browser language (see rule #5)
    const perfectMatchForLocale = Object.values(isoLocales).find((lang) => lang.locale === browserLocale);

    // check rules #2 and #3 (see above)
    const isAlreadyAlternative = () => {
      // if the browser locale has a country code then use the country code, otherwise use the language code
      const langOrCountryCode = browserLocale.includes("-") ? browserLocale.split("-")[1] : browserLocale;

      // CH exception: if the browser language is "fr-CH" and the user is currently on the "de_CH" shop (or vice versa) then return false anyway
      if (langOrCountryCode === "CH" && ["fr-CH", "de-CH"].includes(shopLocale.locale)) {
        return false;
      }

      // check for all our isoLocales that use this language/country code
      // check if the current shop language appears in the hasLangOrCountryCode array
      return Object.values(isoLocales).some(
        (value: LocalDataType): boolean =>
          value.locale.includes(langOrCountryCode) && value.locale === shopLocale.locale,
      );
    };

    if (isAlreadyAlternative() || (shopId === 100005 && !perfectMatchForLocale)) {
      browserStorage.write("isoLocale", shopLocale.locale);

      return;
    }

    const root = createRoot(geoLocationDiv);
    root.render(<GeoLocationModal />);
  }
}

export default GeoLocationModalPortal;

const GeoLocationModal: React.FC = () => {
  const {t} = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [currentShop, setCurrentShop] = useState<ShopInfo | null>(null);
  const [shopSuggestion, setShopSuggestion] = useState<ShopInfo | null>(null);
  const ucUserInteraction = localStorage.getItem("uc_user_interaction");

  const foundMatch = useMemo<[string, LocalDataType] | undefined>(() => {
    // This gives you: `[key, isoLocaleObject][]`
    // KEEP IN MIND: This 'sorts' the entries by key, since these keys are of type `number`, making this an _arrayish_
    // object (with indices _not_ starting at 0). This is of interest for the first `.find()` in the default switch case
    const entriesArray = Object.entries(isoLocales);

    // get the shop ID that matches the browser language setting
    let entry = entriesArray.find(([, value]) => value.locale === browserLocale);

    // if no match is found use the best match, or the COM shop as shop suggestion
    if (!entry) {
      // Some special cases in belgium, that shouldn't redirect to .com
      // (https://www.fincher.org/Utilities/CountryLanguageList.shtml , maybe not an _absolutely complete_ list)
      switch (browserLocale) {
        case "fr-BE":
          entry = entriesArray.find(([, value]) => value.locale === "fr-FR");
          break;
        case "de-BE":
          entry = entriesArray.find(([, value]) => value.locale === "de-DE");
          break;
        case "nl-BE":
          entry = entriesArray.find(([, value]) => value.locale === "nl-NL");
          break;
        default:
          const langOrCountryCode = browserLocale.split("-")[1] || browserLocale;
          entry =
            entriesArray.find(([, value]) => value.locale.includes(langOrCountryCode)) ||
            entriesArray.find(([, value]) => value.locale === "en-US");
          break;
      }
    }

    return entry;
  }, []);

  const getShopInfo = (isoLocale: [string, LocalDataType]) => {
    const getAlternateLinkHref = document.getElementById(`AlternateLink-${isoLocale[0]}`)?.getAttribute("href");
    // when switching to another sales channel, link directly to the respective page by using the equivalent alternate link in the header, otherwise link to the homepage
    const link = getAlternateLinkHref || "https://" + isoLocale[1].url;

    return {
      name: new URL(link).hostname,
      url: link,
      iconUrl: isoLocale[1].flag ? `${cdnDomain[isoLocale[0]]}/media/icons/flags/${isoLocale[1].flag}.svg` : null,
    };
  };

  const openModal = (
    e: CustomEvent<{
      type: "explicit" | "implicit";
      event: "consent_status" | null;
    }>,
  ) => {
    if (e.detail.event === "consent_status" && e.detail.type === "explicit") {
      setIsVisible(true);
    }
  };

  useEffect(() => {
    // Should always be at least _COM_
    if (!foundMatch) return;

    setShopSuggestion(getShopInfo(foundMatch));
    setCurrentShop(getShopInfo([shopId.toString(), shopLocale]));
  }, []);

  useEffect(() => {
    // check cookie banner status before opening the modal (see rule #6)
    if (ucUserInteraction && ucUserInteraction === "true") {
      return setIsVisible(true);
    } else {
      window.addEventListener("ucEvent", openModal);

      return () => window.removeEventListener("ucEvent", openModal);
    }
  }, [shopSuggestion]);

  if (!currentShop || !shopSuggestion) {
    return;
  }

  const handleSwitch = () => {
    window.location.href = shopSuggestion.url;
  };

  const handleStay = () => {
    browserStorage.write("isoLocale", shopLocale.locale);
    setIsVisible(false);
  };

  return (
    <Modal
      isVisible={isVisible}
      customClass={"geolocation-modal"}
      modalTitle={t("general.youAreOn--shop", {shop: currentShop.name})}
      closeFunction={() => setIsVisible(false)}
    >
      <h3>{t("general.youAreOn--shop", {shop: currentShop.name})}</h3>
      <GeoLocationButton
        imagePath={shopSuggestion.iconUrl}
        buttonText={t("general.switchTo--shop", {shop: shopSuggestion.name})}
        clickFunction={handleSwitch}
      />
      <GeoLocationButton
        imagePath={currentShop.iconUrl}
        buttonText={t("general.stayOn--shop", {shop: currentShop.name})}
        clickFunction={handleStay}
      />
    </Modal>
  );
};

const GeoLocationButton: React.FC<{
  imagePath: string | null;
  buttonText: string;
  clickFunction: () => void;
}> = ({imagePath, buttonText, clickFunction}) => {
  return (
    <button className="cta-button cta-inverse" onClick={clickFunction}>
      {imagePath ? (
        <img src={imagePath} alt={""} width={30} height={22} />
      ) : (
        <span className="icon icon-size--22 icon-world">&nbsp;</span>
      )}
      <span>{buttonText}</span>
    </button>
  );
};
